import React from "react";
import styled from "styled-components";
import {
  Card,
  ColumnContainer,
  ElementContainer,
  RowContainer,
} from "../../containers/styledComponents";
import { CompassParagraph } from "../styledComponents";
import { CompanySummary } from "../../../models/CompanyprofileModels";
import { StewardshipFilterParam } from "../../../models/stewardshipModels";
import { useVotingAlignment } from "../../../hooks/useVotingAlignment";
import { VotingTable } from "../../../pages/CompanyProfile/Voting/VotingTable";
import { VotingRecordModal } from "./VotingRecordModal";
import { VotingAlignmentBars } from "./VotingAlignmentBars";
import { LegendCircle } from "../../visuals/styledComponents";
import { votingAlignmentBarsPalette } from "../../../styles";
import { CustomDropdown } from "../../visuals/PageFilter/PageFilter";
import { ToggleAccordionBlueText } from "../../buttons/ToggleAccordionBlueText";

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 100%;
  width: 100%;
  margin-top: 55px;
  justify-content: center;
  padding: 10px;
  border-radius: 8px;
  overflow-y: auto;
`;

const LegendColumnContainer = styled(ColumnContainer)`
  flex-grow: 1;
  align-self: stretch;
`;

const votingAlignmentLegend = ["Aligned Votes", "Misaligned Votes"] as const;

export interface VotingRecordsProps {
  filters: StewardshipFilterParam;
  companySummaryData: CompanySummary | null;
}

export const VotingRecords = ({
  filters,
  companySummaryData,
}: VotingRecordsProps): JSX.Element => {
  const {
    votingAlignmentData,
    dataSource,
    rawVotingData,
    isLoading,
    isCardExpanded,
    shouldDisplayVotingAlignment,
    managerOptions,
    placeholderOption,
    votingTableColumns,
    shouldDisplayVotingRecordModal,
    modalVotingRecordId,
    handleShowVotingRecordModal,
    handleHideVotingRecordModal,
    handleCardExpand,
    setVotingAlignmentReference,
    scrollTargetRef,
  } = useVotingAlignment(filters, true);

  return (
    <RowContainer ml={10} mt={1} ref={scrollTargetRef}>
      <Card w={100} h="auto" m="0 30px 0 10px" isLoading={isLoading}>
        <ElementContainer flexDirection="column">
          <RowContainer mt={-1} jc="flex-start" ai="flex-start">
            <ColumnContainer ai="flex-start" w="75%">
              <CompassParagraph fontSize={16} bold>
                Voting Alignment
              </CompassParagraph>
              <RowContainer mt={8}>
                <CustomDropdown
                  options={managerOptions}
                  placeholder={placeholderOption}
                  onSelectOption={setVotingAlignmentReference}
                  small={true}
                  isLoading={isLoading}
                />
              </RowContainer>
              {shouldDisplayVotingAlignment && (
                <VotingAlignmentBars data={votingAlignmentData} />
              )}
            </ColumnContainer>
            <LegendColumnContainer w="25%">
              {shouldDisplayVotingAlignment && (
                <LegendContainer>
                  {votingAlignmentLegend.map((label, index) => (
                    <ElementContainer key={label} mb={5}>
                      <LegendCircle color={votingAlignmentBarsPalette[index]} />
                      <CompassParagraph color="#8B909A" fontSize={12}>
                        {label}
                      </CompassParagraph>
                    </ElementContainer>
                  ))}
                </LegendContainer>
              )}
            </LegendColumnContainer>
          </RowContainer>
          {shouldDisplayVotingAlignment && (
            <RowContainer mb={-1}>
              <ToggleAccordionBlueText
                handleChange={handleCardExpand}
                isOpen={isCardExpanded}
              />
            </RowContainer>
          )}
          <RowContainer mb={-1}>
            {isCardExpanded && !isLoading && (
              <VotingTable
                data={dataSource}
                handleShowVotingRecordModal={handleShowVotingRecordModal}
                columns={votingTableColumns}
              />
            )}
          </RowContainer>
        </ElementContainer>
      </Card>
      <VotingRecordModal
        shouldDisplay={shouldDisplayVotingRecordModal}
        onHide={handleHideVotingRecordModal}
        modalRecordId={modalVotingRecordId}
        data={rawVotingData}
        votingTableData={dataSource}
        companySummaryData={companySummaryData}
      />
    </RowContainer>
  );
};
